/**
 * L'agenda du jour.
 *
 * Le patron veut un tableau de bord qui montre « tout ce qu'une secrétaire
 * montrerait à son patron pour piloter sa journée ». La première chose qu'elle
 * annonce, c'est l'heure : « à 9 h le métré chez Durand, à 14 h la réception du
 * ravalement ». Un rendez-vous a donc une **heure**, pas une échéance — c'est
 * toute la différence avec une tâche.
 */

export type AppointmentKind = "visite" | "metre" | "reception" | "livraison" | "autre";

/** Les natures, dans les mots du métier. */
export const APPOINTMENT_KIND_LABELS: Record<AppointmentKind, string> = {
  visite: "Visite client",
  metre: "Métré",
  reception: "Réception de chantier",
  livraison: "Livraison",
  autre: "Rendez-vous",
};

export interface Appointment {
  id: string;
  /** Instant ISO 8601. */
  startsAt: string;
  /** `null` = durée inconnue. On n'invente pas un créneau bloqué. */
  durationMinutes: number | null;
  kind: AppointmentKind;
  title: string;
  clientId: string | null;
  clientName: string | null;
  projectId: string | null;
  projectName: string | null;
  location: string | null;
  notes: string | null;
}

const MINUTE_MS = 60_000;

/**
 * Les bornes d'une journée **locale**, en instants ISO.
 *
 * Locale et non UTC : un rendez-vous à 0 h 30 heure de Paris tombe la veille
 * en UTC, et un agenda qui le rangerait au mauvais jour serait pire que pas
 * d'agenda. C'est la journée du patron qui compte, pas celle du serveur.
 */
export function dayBounds(day: Date = new Date()): { from: string; to: string } {
  const debut = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
  const fin = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1, 0, 0, 0, 0);
  return { from: debut.toISOString(), to: fin.toISOString() };
}

/** L'instant où le rendez-vous se termine, s'il a une durée. */
export function appointmentEnd(a: Pick<Appointment, "startsAt" | "durationMinutes">): Date | null {
  if (a.durationMinutes == null) return null;
  return new Date(Date.parse(a.startsAt) + a.durationMinutes * MINUTE_MS);
}

export type AppointmentMoment = "passe" | "en_cours" | "a_venir";

/**
 * Où en est ce rendez-vous, maintenant.
 *
 * Sans durée, un rendez-vous commencé est dit **passé** dès son heure : on ne
 * sait pas s'il dure encore, et le marquer « en cours » indéfiniment laisserait
 * un 9 h allumé jusqu'au soir. Le prochain rendez-vous, lui, compte plus que
 * celui qui vient de commencer.
 */
export function appointmentMoment(
  a: Pick<Appointment, "startsAt" | "durationMinutes">,
  now: Date = new Date(),
): AppointmentMoment {
  const debut = Date.parse(a.startsAt);
  if (now.getTime() < debut) return "a_venir";
  const fin = appointmentEnd(a);
  if (fin && now.getTime() < fin.getTime()) return "en_cours";
  return "passe";
}

/**
 * Le prochain rendez-vous — celui qu'on annonce en premier.
 *
 * Un rendez-vous en cours passe devant : on est dedans. Sinon le premier à
 * venir. `null` quand la journée est finie, ce qui est une réponse.
 */
export function nextAppointment<T extends Pick<Appointment, "startsAt" | "durationMinutes">>(
  list: readonly T[],
  now: Date = new Date(),
): T | null {
  const tries = [...list].sort((x, y) => Date.parse(x.startsAt) - Date.parse(y.startsAt));
  return (
    tries.find((a) => appointmentMoment(a, now) === "en_cours") ??
    tries.find((a) => appointmentMoment(a, now) === "a_venir") ??
    null
  );
}
